import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Container, Typography, Card, CardContent, Button, Box, Grid,
  LinearProgress, List, ListItem, ListItemText, Chip, Snackbar, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { formatFileSize, formatDate } from "../utils";
import HistoryTable from "../components/HistoryTable";
import * as api from "../services/api";

const TranscriptionView = ({ latestWebSocketMessage, isConnected }) => {
  const [audioFile, setAudioFile] = useState(null);
  const [transcriptionQueue, setTranscriptionQueue] = useState([]);
  const [transcriptionProgress, setTranscriptionProgress] = useState({});
  const [transcriptionHistory, setTranscriptionHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Función para buscar la cola de transcripción
  const fetchTranscriptionQueue = useCallback(async () => {
    try {
      // Este endpoint aún no existe, deberá implementarse en el backend
      const response = await axios.get('/api/transcribe/queue');
      setTranscriptionQueue(response.data);
    } catch (err) {
      console.error("Error al obtener cola de transcripción:", err);
      if (err.response?.status === 404) {
        // El endpoint no existe aún
        console.log("El endpoint para cola de transcripción aún no está implementado");
        setTranscriptionQueue([]);
      } else {
        setError("Error al cargar la cola de transcripción");
      }
    }
  }, []);

  // Función para buscar el historial de transcripciones
  const fetchTranscriptionHistory = useCallback(async () => {
    try {
      // Este endpoint aún no existe, deberá implementarse en el backend
      const response = await axios.get('/api/transcribe/history');
      setTranscriptionHistory(response.data);
    } catch (err) {
      console.error("Error al obtener historial de transcripción:", err);
      if (err.response?.status === 404) {
        // El endpoint no existe aún
        console.log("El endpoint para historial de transcripción aún no está implementado");
        setTranscriptionHistory([]);
      } else {
        setError("Error al cargar el historial de transcripción");
      }
    }
  }, []);

  // Carga inicial de datos
  useEffect(() => {
    fetchTranscriptionQueue();
    fetchTranscriptionHistory();
    
    // Configura un intervalo para actualizar si no usamos WebSockets para esto
    const intervalId = setInterval(() => {
      fetchTranscriptionQueue();
    }, 10000); // Cada 10 segundos
    
    return () => clearInterval(intervalId);
  }, [fetchTranscriptionQueue, fetchTranscriptionHistory]);

  // Responder a mensajes WebSocket
  useEffect(() => {
    if (latestWebSocketMessage) {
      if (latestWebSocketMessage.type === 'transcriptionProgress') {
        setTranscriptionProgress(prev => ({
          ...prev,
          [latestWebSocketMessage.filename]: latestWebSocketMessage.data
        }));
      } else if (latestWebSocketMessage.type === 'transcriptionQueueUpdate') {
        fetchTranscriptionQueue();
      } else if (latestWebSocketMessage.type === 'transcriptionComplete') {
        setTranscriptionProgress(prev => ({
          ...prev,
          [latestWebSocketMessage.filename]: { 
            ...prev[latestWebSocketMessage.filename],
            completed: true 
          }
        }));
        fetchTranscriptionHistory();
        fetchTranscriptionQueue();
        setSuccessMessage(`Transcripción completada para ${latestWebSocketMessage.originalFilename}`);
      } else if (latestWebSocketMessage.type === 'transcriptionError') {
        setError(`Error durante la transcripción de ${latestWebSocketMessage.originalFilename}: ${latestWebSocketMessage.error}`);
      }
    }
  }, [latestWebSocketMessage, fetchTranscriptionQueue, fetchTranscriptionHistory]);

  // Manejar el cambio de archivo de audio
  const handleAudioFileChange = (e) => {
    setAudioFile(e.target.files[0]);
    setError("");
    setSuccessMessage("");
  };

  // Manejar la subida y transcripción
  const handleUploadAndTranscribe = async (e) => {
    e.preventDefault();
    if (!audioFile) {
      setError("Por favor selecciona un archivo de audio primero.");
      return;
    }
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    const formData = new FormData();
    formData.append("audio", audioFile);

    try {
      // Este endpoint aún no existe, deberá implementarse en el backend
      const response = await axios.post('/api/transcribe/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccessMessage(response.data?.message || "Archivo subido y transcripción iniciada.");
      setAudioFile(null); // Limpiar el input
      fetchTranscriptionQueue(); // Refrescar la cola
    } catch (err) {
      console.error("Error al subir audio:", err);
      if (err.response?.status === 404) {
        // El endpoint no existe aún
        setError("Esta funcionalidad aún no está implementada en el backend.");
      } else {
        setError(err.response?.data?.error || "Error al subir el archivo de audio.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Función para formatear la duración (mm:ss)
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Función para limpiar el historial de transcripción
  const handleClearTranscriptionHistory = async () => {
    try {
      await api.clearTranscriptionHistory();
      fetchTranscriptionHistory();
      setSuccessMessage("Historial de transcripción limpiado correctamente");
    } catch (error) {
      console.error('Error al limpiar historial de transcripción:', error);
      setError("Error al limpiar el historial de transcripción");
    }
  };

  // Función para eliminar un archivo específico de transcripción
  const handleDeleteTranscriptionItem = async (item) => {
    try {
      await api.deleteTranscriptionItem(item.processedFilename);
      fetchTranscriptionHistory();
      setSuccessMessage(`Transcripción de ${item.originalFilename} eliminada correctamente`);
    } catch (error) {
      console.error('Error al eliminar transcripción:', error);
      setError("Error al eliminar la transcripción");
    }
  };

  // Función para previsualizar la transcripción
  const handlePreviewTranscription = async (filename) => {
    try {
      // Implementar la previsualización de transcripción
      console.log("Previsualizar transcripción:", filename);
      // Esta función se implementará más adelante
    } catch (error) {
      console.error('Error al previsualizar transcripción:', error);
      setError("Error al previsualizar la transcripción");
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h5" component="h1" gutterBottom>
        Generar Transcripción desde Audio
      </Typography>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Conectando al servidor WebSocket...
        </Alert>
      )}

      {/* Sección de Subida */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Subir Archivo de Audio</Typography>
          <form onSubmit={handleUploadAndTranscribe}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                >
                  {audioFile ? audioFile.name : "Seleccionar Archivo de Audio (.mp3, .wav, ...)"}
                  <input
                    type="file"
                    hidden
                    onChange={handleAudioFileChange}
                    accept="audio/mpeg, audio/wav, audio/*"
                  />
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!audioFile || isLoading}
                >
                  {isLoading ? "Subiendo..." : "Subir y Transcribir"}
                </Button>
              </Grid>
            </Grid>
            {isLoading && <LinearProgress sx={{mt: 2}} />}
          </form>
        </CardContent>
      </Card>

      {/* Cola de Transcripción */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Cola de Transcripción</Typography>
          {transcriptionQueue.length === 0 ? (
            <Typography color="text.secondary">Cola de transcripción vacía.</Typography>
          ) : (
            <List dense>
              {transcriptionQueue.map((item) => {
                const progress = transcriptionProgress[item.id || item.originalFilename] || {};
                return (
                  <ListItem key={item.id || item.originalFilename} divider>
                    <ListItemText
                      primary={item.originalFilename}
                      secondary={progress.status || item.status || 'En espera...'}
                    />
                    {progress.current && progress.total && !progress.completed && (
                      <Box sx={{ width: '50%', mr: 1 }}>
                        <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100} />
                        <Typography variant="caption">{`${progress.current}/${progress.total}`}</Typography>
                      </Box>
                    )}
                    {progress.completed && <Chip label="Procesamiento Finalizado" color="info" size="small" sx={{ml: 1}} />}
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Historial de Transcripción */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Historial de Transcripción</Typography>
          {transcriptionHistory.length === 0 ? (
            <Typography color="text.secondary">No hay historial de transcripción disponible.</Typography>
          ) : (
            <HistoryTable 
              history={transcriptionHistory.map(item => ({
                ...item,
                originalFilename: item.originalFilename,
                processedFilename: item.processedFilename,
                operation: "Transcripción",
                processedDate: item.completedDate,
                downloadLink: item.downloadLink,
                downloadLinks: item.downloadLinks || {
                  json: item.downloadLink,
                  txt: `${item.downloadLink}?format=txt`
                }
              }))} 
              onPreview={handlePreviewTranscription}
              onClearHistory={handleClearTranscriptionHistory}
              onDeleteItem={handleDeleteTranscriptionItem}
              clearHistoryLabel="Limpiar historial de transcripción"
            />
          )}
        </CardContent>
      </Card>

      {/* Snackbars para Feedback */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert onClose={() => setError('')} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSuccessMessage('')} severity="success" variant="filled" sx={{ width: '100%' }}>{successMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default TranscriptionView; 